import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { DoorOpen, Lock, Map, X } from "lucide-react";
import { MONORAIL, worldCount } from "../lib/monorail";

/**
 * The park map.
 *
 * Disney used to hand you a paper map on the way in, and you kept it for the
 * rest of the day. Completing the Tour leaves this in the corner permanently:
 * it turns a one-time ride into standing navigation, and it is what makes this
 * read as a place rather than an app.
 */
export const WorldMap = () => {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { places, open: openCount } = worldCount();

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="world-map-button"
        onClick={() => setOpen((v) => !v)}
        title={`${places} Places · ${openCount} Open`}
        className="glass flex h-10 items-center gap-1.5 rounded-full px-3.5 transition-transform duration-200 hover:scale-105"
      >
        <Map size={14} className="text-[var(--stage-gold)]" />
        <span className="mono text-[9px] uppercase tracking-[0.18em] text-white/75">
          World
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="glass fixed bottom-[4.5rem] right-4 z-50 w-[290px] max-w-[calc(100vw-2rem)] rounded-2xl p-5 md:bottom-[5.5rem] md:right-6"
            data-testid="world-map-panel"
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <div className="tracked text-[9px] text-[var(--stage-gold)]">
                  {places} Places
                </div>
                <div className="mono mt-0.5 text-[9px] text-white/35">
                  {openCount} Open · the rest Not Open Yet
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/25 transition-colors hover:text-white/70"
                aria-label="close"
              >
                <X size={13} />
              </button>
            </div>

            <div className="space-y-0.5">
              {MONORAIL.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`map-place-${p.key}`}
                  disabled={!p.open}
                  onClick={() => {
                    setOpen(false);
                    nav(p.to);
                  }}
                  className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left transition-colors duration-200 enabled:hover:bg-white/[0.07] disabled:cursor-default"
                >
                  <span
                    className="text-[13px]"
                    style={{ color: p.open ? "#f2e6d0" : "rgba(255,255,255,0.3)" }}
                  >
                    {p.name}
                  </span>
                  {p.open ? (
                    <DoorOpen size={11} className="shrink-0 text-[var(--stage-gold)]" />
                  ) : (
                    <Lock size={10} className="shrink-0 text-white/15" />
                  )}
                </button>
              ))}
            </div>

            <button
              type="button"
              data-testid="map-retake-tour"
              onClick={() => {
                setOpen(false);
                nav("/tour");
              }}
              className="mono mt-4 text-[9px] text-white/30 transition-colors hover:text-[var(--stage-gold)]"
            >
              → take the tour again
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorldMap;
